import { useState } from 'react';

import { type InvitedAppShortcutRecommendedAppInput } from '../../common/types';
import { type ExperimentDetails, type FullContext, IneligibilityReason } from './types';
import {
	ineligible,
	type ResolvedCacheDetails,
	resolveFromRecommendations,
} from './utils/invited-app-shortcuts-resolver';

export type UseInvitedAppShortcutsInput =
	| ({ enabled: false } & Partial<UseInvitedAppShortcutsEnabledInput>)
	| ({ enabled: true } & UseInvitedAppShortcutsEnabledInput);

export type UseInvitedAppShortcutsEnabledInput = FullContext & {
	/**
	 * Target apps recommended by `InvitedAppShortcutsRecommendationsProvider`.
	 * The hook adapts these into `invitedApps` for downstream consumers; only
	 * recommended apps are present in `invitedApps`.
	 *
	 * Accepts either bare product keys or `{ product, variant }` objects. Bare
	 * keys normalise to `variant: 'none'`; pass the object form to carry the
	 * lozenge variant parsed from the GRS `entityId` through to `invitedApps`.
	 */
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput>;
};

export type UseInvitedAppShortcutsResult = ExperimentDetails;

/**
 * Main hook to return eligibility for Invited App Shortcuts.
 *
 * Eligibility is derived entirely from the caller-supplied `recommendedApps`, which come from
 * `InvitedAppShortcutsRecommendationsProvider` (GRS, preloaded during SSR). The result is read once
 * and then stable, so it never re-renders the nav: `invitedApps` contains an entry per recommended
 * app, and an empty `recommendedApps` yields `ineligibilityReason: 'NO_RECOMMENDED_APPS'`.
 *
 * `collaborators` and `firstImpressionAt` are always `undefined` here — collaborators are loaded
 * lazily on hover by the Relay inner container, and impression state is resolved by GRS from the
 * TAP `first_impression_at` trait and delivered as the lozenge variant.
 *
 * If the `enabled` option is false, this hook does nothing.
 */
export const useInvitedAppShortcuts = (
	input: UseInvitedAppShortcutsInput,
): UseInvitedAppShortcutsResult => {
	const { enabled } = input;
	const recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput> = enabled
		? input.recommendedApps
		: [];

	// Read once and then stable (non-reactive), ensured by useState with a functional initializer.
	// This implementation prioritizes performance: it has no dynamic React state at all.
	const [cached] = useState<ResolvedCacheDetails>(() =>
		enabled
			? resolveFromRecommendations({ recommendedApps })
			: { eligibility: ineligible(IneligibilityReason.EXPERIMENT_OFF) },
	);

	return cached.eligibility;
};
